import {Box} from '@mui/material';
import moment from 'moment-timezone';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useRouteMatch} from 'react-router-dom';
import {i18n} from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import layoutActions from 'src/modules/layout/layoutActions';
import layoutSelectors from 'src/modules/layout/layoutSelectors';
import Errors from 'src/modules/shared/error/errors';
import {getHistory} from 'src/modules/store';
import {default as actions} from 'src/modules/tarea/form/tareaFormActions';
import selectors from 'src/modules/tarea/form/tareaFormSelectors';
import TareaService from 'src/modules/tarea/tareaService';
import ModeloTareaService from 'src/modules/modeloTarea/modeloTareaService';
import {omitMedicionesParcialIfUnused} from 'src/modules/tarea/medicionesParcialId23';
import ContentWrapper from 'src/view/layout/styles/ContentWrapper';
import Breadcrumb from 'src/view/shared/Breadcrumb';
import Spinner from 'src/view/shared/Spinner';
import PageTitle from 'src/view/shared/styles/PageTitle';
import TareaForm from 'src/view/tarea/form/TareaForm';

/**
 * Helpers: whitelist para UPDATE
 * (porque el backend hace updateOne({ ...data }) y si mandas basura, pisa campos reales)
 */
const pick = (obj: any, keys: string[]) => {
  if (!obj) return {};
  return keys.reduce((acc: any, key) => {
    if (obj[key] !== undefined) acc[key] = obj[key];
    return acc;
  }, {});
};

const normalizeId = (value: any) => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return value.id || value._id;
  return undefined;
};

const sanitizeListaProcedimientoItem = (item: any) => {
  if (!item) return item;

  // Las preguntas tienen demasiadas variantes por tipo de respuesta;
  // no se recortan para evitar pérdida de datos relevantes en updates.
  if (item.tipo === 'pregunta') {
    return item;
  }
  return item;
};

const sanitizeUpdatePayload = (data: any, tenantId?: string | null) => {
  if (!data) return data;

  omitMedicionesParcialIfUnused(data, {
    tenantId,
    modeloTareaTitulo: data.modeloTarea?.titulo || data.tituloTarea,
    primeroDelParcial: data.primeroDelParcial,
  });

  const clean: any = pick(data, [
    // lógicas directas del update()
    'estado',
    'firmasListas',
    'motivoRechazo',
    'silent',
    'autosave',
    'skipCalendarSync',
    'app',

    // mantenimiento
    'kilometraje',
    'horometro',
    'fechaHorometro',

    // notificaciones/activation
    'isActive',
    'ejecutor',

    // procedimiento + OCR + reglas
    'listaProcedimiento',

    // Estandar: comentario, documentos, fotos
    'comentarioEnviado',
    'documentosEnviados',
    'fotosEnviadas',

    // Pasos / Checklist
    'respuestaInterfaz',

    // si tu front manda esto para controlar el cierre de drawer (lo usas arriba)
    'updating',
    'comienzo',
    'fin',
    'enviada',
    'keepDrawersOpen',

    'divisionId',
    'medicionesParcial',
    //folio
    'customSequenceId',
    'customSequencePrefix',
    'removeNumeroCustom',
    'applyFolioToChildren',
  ]);

  // normalizar ejecutor
  if (clean.ejecutor) {
    clean.ejecutor = {id: normalizeId(clean.ejecutor)};
  }

  // limpiar listaProcedimiento pero manteniendo lo necesario
  if (Array.isArray(clean.listaProcedimiento)) {
    clean.listaProcedimiento = clean.listaProcedimiento.map(sanitizeListaProcedimientoItem);
  }

  // IMPORTANTÍSIMO: bloquear cosas que NO deberían viajar jamás en un update
  // (aunque vengan dentro de data porque tu form arma un monstruo)
  delete clean.modeloTarea;
  delete clean.tipoVisualizacion;
  delete clean.clasificacion;

  return clean;
};

function TareaFormPage(props) {
  const [dispatched, setDispatched] = useState(false);
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const currentUser = useSelector(authSelectors.selectCurrentUser);
  const currentTenant = useSelector(authSelectors.selectCurrentTenant);

  const [initLoading, setInitLoading] = useState(true);
  const saveLoading = useSelector(selectors.selectSaveLoading);
  const [record, setRecord] = useState<any>(null);

  const [isTaskEditing, setIsTaskEditing] = useState(false);
  const isEditing = Boolean(props.tarea?.id || match.params.id || isTaskEditing);
  const title = isTaskEditing
    ? i18n('entities.tarea.editClosed.title')
    : isEditing
      ? i18n('entities.tarea.edit.title')
      : i18n('entities.tarea.new.title');

  const init = async () => {
    try {
      setInitLoading(true);

      const id = props.tarea?.id || match.params.id;
      const isEdit = Boolean(id);
      const modeloTareaOrId = props.modeloTarea || props.modeloTareaId;
      let result: any = {};

      if (isEdit) {
        result = await TareaService.find(id);
      } else if (modeloTareaOrId) {
        const modeloTarea =
          typeof modeloTareaOrId === 'object' && modeloTareaOrId?.id
            ? modeloTareaOrId
            : await ModeloTareaService.find(
                typeof modeloTareaOrId === 'string' ? modeloTareaOrId : modeloTareaOrId?.id,
              );
        if (modeloTarea) {
          result = {
            modeloTarea,
            clasificacion: modeloTarea.clasificacion || 'incidente',
            tipoVisualizacion: modeloTarea.tipoVisualizacion,
            procedimientoInterfaz: modeloTarea.procedimiento?.formato,
            ejecutor: currentUser ? {id: currentUser.id} : undefined,
            estado: 'Abierta',
          };
        }
      }
      setRecord(result);

      setInitLoading(false);
      setDispatched(true);
    } catch (error) {
      Errors.handle(error);
    }
  };

  const layout: any = useSelector(layoutSelectors.selectRaw);

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (
      layout.drawerUpdateComponent === 'TareaFormPage' &&
      props.tarea?.id &&
      layout.recordId &&
      layout.recordId === props.tarea?.id
    ) {
      init();
    }
  }, [dispatch, layout.drawerChanged]);

  const doSubmit = async (id, data) => {
    if (!id && data?.updating) {
      return;
    }
    if (isEditing) {
      // 🔥 acá está el win: recortamos el payload del update con whitelist
      const sanitized = sanitizeUpdatePayload(
        {
          ...data,
          ...(props.keepDrawersOpen ? {keepDrawersOpen: true} : {}),
        },
        currentTenant?.id,
      );

      const result = await dispatch(actions.doUpdate(id, sanitized, props.edited));

      if (result && props.onRecordSaved) {
        props.onRecordSaved({updating: Boolean(sanitized.updating)});
      }

      if (!sanitized.updating && !props.keepDrawersOpen) {
        setTimeout(() => {
          dispatch(
            layoutActions.doVisibleDrawer({
              drawerId: 'DrawerTareaFormPage',
              drawerVisible: false,
              drawerChanged: moment().unix(),
              drawerUpdateComponent: 'TareaListPage',
            }),
          );
        }, 500);
      }

      return result;
    } else {
      const createData = {...data};
      if (props.drawerId) createData.drawerId = props.drawerId;
      if (props.drawerUpdateComponent) createData.drawerUpdateComponent = props.drawerUpdateComponent;
      if (props.createFromWebIncidente) {
        createData.createFromWebIncidente = true;
        createData.createIncident = true;
      }
      return dispatch(actions.doCreate(createData, false));
    }
  };

  return (
    <>
      {!props.drawer && !props.disabled && (
        <Breadcrumb items={[[i18n('dashboard.menu'), '/'], [i18n('entities.tarea.menu'), '/tarea'], [title]]} />
      )}
      {initLoading ? (
        <ContentWrapper>
          <PageTitle>{i18n('common.loading')}</PageTitle>
          <Box>
            <Spinner />
          </Box>
        </ContentWrapper>
      ) : (
        dispatched &&
        !initLoading && (
          <TareaForm
            key={(record?.id || record?.modeloTarea?.id || 'new') + (isTaskEditing ? 'editing' : '')}
            drawer={props.drawer}
            createFromWebIncidente={props.createFromWebIncidente}
            tarea={props.tarea}
            isTaskEditing={isTaskEditing}
            setIsTaskEditing={setIsTaskEditing}
            saveLoading={saveLoading}
            initLoading={initLoading}
            record={record}
            isEditing={isEditing}
            onSubmit={doSubmit}
            onCancel={() => getHistory().goBack()}
            hideComunicacionInterna={props.hideComunicacionInterna}
            title={title}
            setRecord={setRecord}
          />
        )
      )}
    </>
  );
}

export default TareaFormPage;
